import { createContext, useContext, useMemo, PropsWithChildren } from "react";
import type {
  StepConfig,
  FieldSchema,
  FieldOption,
} from "../types/workflow-schema";
import { getFieldsFromConfig } from "../types/workflow-schema";

type StepConfigContextValue = {
  stepConfig: StepConfig | null;
  getLabel: (fieldName: string, fallback: string) => string;
  getPlaceholder: (fieldName: string, fallback: string) => string;
  getDescription: (fieldName: string, fallback: string) => string;
  getOptions: (fieldName: string, fallback: FieldOption[]) => FieldOption[];
  getTextBlock: (key: string, fallback: string) => string;
};

const defaultContext: StepConfigContextValue = {
  stepConfig: null,
  getLabel: (_, fallback) => fallback,
  getPlaceholder: (_, fallback) => fallback,
  getDescription: (_, fallback) => fallback,
  getOptions: (_, fallback) => fallback,
  getTextBlock: (_, fallback) => fallback,
};

const StepConfigContext = createContext<StepConfigContextValue>(defaultContext);

export const StepConfigProvider = ({
  stepConfig,
  children,
}: PropsWithChildren<{ stepConfig: StepConfig | null }>) => {
  const value = useMemo<StepConfigContextValue>(() => {
    if (!stepConfig) return defaultContext;

    const fieldMap = new Map<string, FieldSchema>();
    // Include fields[] first (lower priority — used for metadata/options on component steps)
    stepConfig.fields?.forEach((f) => fieldMap.set(f.name, f));
    // Then overwrite with contentBlocks-derived fields (higher priority)
    getFieldsFromConfig(stepConfig).forEach((f) => fieldMap.set(f.name, f));

    return {
      stepConfig,
      getLabel: (name, fallback) => fieldMap.get(name)?.label ?? fallback,
      getPlaceholder: (name, fallback) =>
        fieldMap.get(name)?.placeholder ?? fallback,
      getDescription: (name, fallback) =>
        fieldMap.get(name)?.description ?? fallback,
      getOptions: (name, fallback) => {
        const cmsOptions = fieldMap.get(name)?.options;
        if (!cmsOptions?.length) return fallback;
        return cmsOptions;
      },
      getTextBlock: (key, fallback) => stepConfig.textBlocks?.[key] ?? fallback,
    };
  }, [stepConfig]);

  return (
    <StepConfigContext.Provider value={value}>
      {children}
    </StepConfigContext.Provider>
  );
};

export const useFieldConfig = () => useContext(StepConfigContext);

/**
 * Layers block-level textBlocks on top of step-level textBlocks.
 * When a content block carries its own textBlocks, wrap its renderer
 * with this provider so `getTextBlock()` checks block-level first,
 * then falls back to step-level. This makes blocks portable — if a
 * CMS editor moves a block to another step, its text follows it.
 */
export const BlockTextProvider = ({
  textBlocks,
  children,
}: PropsWithChildren<{ textBlocks?: Record<string, string> }>) => {
  const parent = useContext(StepConfigContext);

  const value = useMemo<StepConfigContextValue>(() => {
    if (!textBlocks || Object.keys(textBlocks).length === 0) return parent;

    return {
      ...parent,
      getTextBlock: (key, fallback) =>
        textBlocks[key] ?? parent.getTextBlock(key, fallback),
    };
  }, [parent, textBlocks]);

  return (
    <StepConfigContext.Provider value={value}>
      {children}
    </StepConfigContext.Provider>
  );
};
